<template>

    <ui-view class="view-section-form" v-spinner="loading" :hide="loading">

        <ui-heading :title="pageTitle" icon="magicon-tiles">
            <ui-button
                v-if="$can('MUTATE_SECTIONS')"
                color="positive"
                :loading="submitting"
                @click="$refs.form.submit()">Opslaan</ui-button>
            <ui-button
                v-if="sectionId && $can('MUTATE_SECTIONS')"
                color="negative"
                icon="magicon-trash"
                @click="deleteSection">Verwijderen</ui-button>

            <ui-button
                color="medium"
                type="bordered"
                @click="$router.go(-1)"
                slot="left">Annuleren</ui-button>
        </ui-heading>

        <ui-form-panel auto-validate ref="form" @submit="submit">

            <ui-form-dropdown
                searchable
                title="Perceel"
                name="parcelId"
                :options="parcelOptions"
                rules="required"
                v-model="pendingSection.parcelId"/>

            <ui-form-input
                title="Naam"
                name="name"
                rules="required"
                v-model="pendingSection.name"/>

            <ui-form-input
                title="Oppervlakte (ha)"
                name="size"
                rules="required"
                decimal
                :hint="availableSize != null ? `Beschikbare oppervlakte: ${ formattedAvailableSize } ha` : null"
                v-model="pendingSection.size"/>

        </ui-form-panel>

    </ui-view>

</template>

<script>

import Vue from 'vue';
import { clone, pick } from 'es-toolkit';
import Component from 'vue-class-component';

import createQuery from 'js-core/js/apollo/queries/createQuery';

import AllParcelsQuery from 'gql/queries/SectionForm/AllParcels.gql';
import FindSectionQuery from 'gql/queries/SectionForm/FindSection.gql';

import SectionMutations from 'app/mutations/SectionMutations';

@Component({

    name: 'SectionForm',
    apollo: {
        section: createQuery(FindSectionQuery, {

            skip() {

                return !this.sectionId;
            },

            variables() {

                return {
                    id: this.sectionId
                };
            },

            response(response) {

                this.pendingSection = pick(response, Object.keys(this.pendingSection));
            }
        }),
        parcels: createQuery(AllParcelsQuery)
    }
})
export default class SectionForm extends Vue
{
    loading = 0;
    submitting = false;

    pendingSection = {
        parcelId: null,
        name: null,
        size: null
    };

    breadcrumb(){

        return [{
            title: 'Vakken',
            route: {
                name: 'sections'
            }
        }, this.pageTitle];
    }

    get selectedParcel() {

        if(!this.parcels) {
            return null;
        }

        return this.parcels.items.find(parcel => parcel.id === this.pendingSection.parcelId);
    }

    get availableSize() {

        if(!this.selectedParcel) {
            return null;
        }

        return this.selectedParcel.size - this.selectedParcel.usedSize;
    }

    get formattedAvailableSize() {

        if(this.availableSize === null) {
            return null;
        }

        return this.availableSize.toFixed(2).replace('.', ',');
    }

    async submit() {

        if(!this.$can('MUTATE_SECTIONS')) {
            return;
        }

        let input = clone(this.pendingSection);

        if(this.availableSize !== null && input.size > this.availableSize) {
            this.$toast.error('De oppervlakte mag niet groter zijn dan de beschikbare oppervlakte, vergroot de oppervlakte van het perceel of verwijder een vak');
            return;
        }

        this.submitting = true;

        try {

            if(this.sectionId) {

                input.id = this.sectionId;

                await SectionMutations.update(input);

                this.logger.info('Section updated');
                this.$toast.success('Het vak is aangepast');
            }
            else {

                await SectionMutations.create(input);

                this.logger.info('Section created');
                this.$toast.success('Het vak is aangemaakt');
            }

            this.$router.replace({
                name: 'sections'
            });
        }
        catch(e) {

            this.logger.error('Error creating/updating section', e);
            this.$toast.error('Het vak kon niet worden opgeslagen');
        }

        this.submitting = false;
    }

    async deleteSection() {

        if(!this.$can('MUTATE_SECTIONS')) {
            return;
        }

        const { result, overlay } = await this.$alert.delete('Weet je zeker dat je dit vak wilt verwijderen?', true, {
            subtitle: 'Ook alle voorgaande acties zullen worden verwijderd!'
        });

        if(!result) {
            return;
        }

        const id = this.sectionId;
        try {

            await SectionMutations.delete(id);

            this.$toast.success('Het vak is succesvol verwijderd');
            this.$router.replace({ name: 'sections' });
        }
        catch(e) {

            this.logger.error(`Something went wrong while deleting section ${id}:`, e);
            this.$toast.error('Er is iets fout gegaan tijdens het verwijderen van het vak');
        }

        this.$overlay.close(overlay);
    }

    get sectionId() {

        return this.$route.params.id;
    }

    get pageTitle(){

        return this.sectionId ? this.$safe('section.name') : 'Vak aanmaken';
    }

    get parcelOptions() {

        if(!this.parcels) {
            return [];
        }

        return this.parcels.items.map(parcel => ({
            label: `${parcel.parcelLocation.name} ${parcel.parcelLocation.number}` + (parcel.alias ? ` - ${parcel.alias}` : ''),
            value: parcel.id
        }));
    }
}

</script>